import axios from 'axios';
import { retry } from '../utils/promise-helpers';

const url = process.env.REACT_APP_API_URL || 'http://localhost:4000/api',
	api = axios.create({ baseURL: url }),
	apiBackground = axios.create({ baseURL: url }),
	getSessionState = async () => {
		const { store } = await import('src/states/store');
		const state = store.getState();
		return state.session;
	},
	refreshTokenDispatch = (refreshToken) =>
		apiBackground
			.post('/users/refresh-token', { refreshToken })
			.then((response) => response.data)
			.then((data) => data.body),
	logOut = async () => {
		const { store } = await import('src/states/store');
		const { logout } = await import('src/states/modules/session');
		store.dispatch(logout());
	},
	updateStore = async (data) => {
		const { token, refreshToken } = data;

		const { store } = await import('src/states/store');
		const { setNewRefreshTokenSuccess } = await import('src/states/modules/session');
		store.dispatch(setNewRefreshTokenSuccess(data));

		api.defaults.headers.Authorization = `Bearer ${token}`;
		return token;
	};

//An unauthorized request means the token has expired. We will try to get a new one with our valid refreshToken.
//If we succeed the new token and refreshToken wll be saved in LocalStore and we resend the request.
//Otherwise, if it fails more than 3 times or if the refreshToken is expired the user will be logged out.
api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const { status, config } = error.response || {};
		const state = await getSessionState();
		const { refreshToken, signed } = state;

		// Prevent infinite loops - if this request was already retried, don't try again
		if (config._retry) {
			return Promise.reject(error);
		}

		// Handle 401 - Token expired, try to refresh
		if (status === 401 && signed) {
			// Mark this request as retried
			config._retry = true;

			return retry(3, 500, refreshTokenDispatch, refreshToken)
				.then(updateStore)
				.then((token) => {
					if (token) {
						config.headers.Authorization = `Bearer ${token}`;
						return api.request(config);
					}
				})
				.catch(async (err) => {
					await logOut();
					// Redirect to login when token refresh fails
					window.location.href = '/login';
					throw err;
				});
		}

		// Handle 403 - Forbidden
		// Only try to refresh token if we haven't already tried for this request
		// This prevents infinite loops when 403 is due to lack of permissions, not expired token
		if (status === 403 && signed && refreshToken && !config._refreshAttempted) {
			// Mark that we attempted refresh for this request
			config._refreshAttempted = true;

			try {
				const newTokenData = await retry(3, 500, refreshTokenDispatch, refreshToken);
				await updateStore(newTokenData);

				// If refresh succeeded, retry the original request ONCE
				const newState = await getSessionState();
				if (newState.token) {
					config.headers.Authorization = `Bearer ${newState.token}`;
					config._retry = true; // Mark as retried to prevent further attempts
					return api.request(config);
				}
			} catch (refreshError) {
				// Refresh failed - token is expired, logout and redirect
				await logOut();
				// Redirect to login page
				window.location.href = '/login';
				return Promise.reject(error);
			}
		}

		return Promise.reject(error);
	},
);

export const getApiUrl = () => url;
export default api;
